import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { PremiumName } from '../../components/PremiumName';
import { Sheet } from '../../components/Sheet';
import { EmptyState, ErrorState, PageLoader } from '../../components/States';
import { useToasts } from '../../components/Toasts';
import { errorMessage } from '../../services/api';
import { useConversations, useFriends, useSendMessage, useStartConversation } from '../../services/queries';
import { messageClientId } from '../social/labels';

interface ShareCardSheetProps {
  open: boolean;
  onClose: () => void;
  characterId: number;
  characterName: string;
}

export function ShareCardSheet({ open, onClose, characterId, characterName }: ShareCardSheetProps) {
  const { t } = useTranslation('collection');
  const friends = useFriends();
  const conversations = useConversations();
  const start = useStartConversation();
  const send = useSendMessage();
  const navigate = useNavigate();
  const toasts = useToasts();
  const busy = start.isPending || send.isPending;

  const shareWith = async (userId: string) => {
    try {
      const existing = conversations.data?.items.find((item) => item.peer.id === userId);
      const conversationId = existing?.id ?? (await start.mutateAsync(userId)).conversationId;
      await send.mutateAsync({
        conversationId,
        sharedCharacterId: characterId,
        clientId: messageClientId(),
      });
      toasts.push({ tone: 'success', title: t('share.shared'), description: characterName });
      onClose();
      navigate(`/messages/${conversationId}`);
    } catch (error) {
      toasts.push({ tone: 'error', title: t('share.failed'), description: errorMessage(error) });
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('share.title', { name: characterName })} showTitle>
      {!open ? null : friends.isPending || conversations.isPending ? (
        <PageLoader />
      ) : friends.isError ? (
        <ErrorState error={friends.error} onRetry={() => void friends.refetch()} />
      ) : (
        <div className="share-panel">
          <button
            type="button"
            className="button button--primary button--block"
            onClick={() => {
              const url = `${window.location.origin}/personnages/${characterId}`;
              void navigator.clipboard?.writeText(url).then(() =>
                toasts.push({ tone: 'success', title: t('share.linkCopied'), description: url }),
              );
            }}
          >
            {t('share.copyLink')}
          </button>
          {friends.data.items.length === 0 ? (
            <EmptyState title={t('share.noFriends')}>{t('share.noFriendsHint')}</EmptyState>
          ) : (
            <ul className="share-friend-list">
              {friends.data.items.map((friend) => (
                <li key={friend.player.id}>
                  <button type="button" className="button" disabled={busy} onClick={() => void shareWith(friend.player.id)}>
                    <Avatar user={friend.player} size={32} />
                    <PremiumName user={friend.player} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Sheet>
  );
}
